import { useLayoutEffect, useRef } from 'react';
import type { RefObject } from 'react';

/**
 * 🔴 THE MODAL BEHAVIOUR, SHARED — design-system plan DS-11a2 (§1.4; F51, F388, F557). Extracted verbatim from
 * `Dialog.tsx` (DS-8), which is its first user; the command palette is its second. What it does to a `<dialog>`:
 *
 * - FOCUS GOES IN: to `initialFocus` when the caller names it, else the first field, else the first button.
 * - TAB STAYS IN: Tab from the last control goes to the first, Shift+Tab from the first to the last. The browser's own
 *   modal lets Tab leave for the address bar; a person tabbing through a till prompt must not lose it.
 * - ESCAPE CANCELS, from anywhere inside it: `onCancel` — never a quiet "yes". It stops there, so a drawer or the
 *   palette listening on the document does not close too.
 * - FOCUS COMES BACK to the element that had it when the dialog opened (the "Void" button, the palette's trigger),
 *   once the dialog has gone.
 * - THE PAGE IS INERT: every other child of `<body>` carries `inert` while it is open, and loses it after — only the
 *   ones this dialog quieted, so a dialog opened over another leaves the first one's work alone.
 *
 * ⚠️ THE CONTRACT: the dialog is a DIRECT CHILD OF `<body>` (`createPortal(…, document.body)`). The page is quieted by
 * making every other child of `<body>` inert, so a dialog drawn inside the app's root would be made inert with it, and
 * be dead (plan C2). Opened once per mount: the caller mounts the dialog while it is open and unmounts it to close.
 */
const TABBABLE = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(', ');

/**
 * The controls Tab visits inside `root`, in document order. ⚠️ Drawn ones only (DS-11a2 review): the palette's Close
 * button is `display: none` above a phone, and a trap whose last stop Tab skips lets Tab out of the dialog. jsdom has no
 * `checkVisibility` (and no layout): there every control counts.
 */
export function tabbables(root: HTMLElement): HTMLElement[] {
  return Array.from(root.querySelectorAll<HTMLElement>(TABBABLE)).filter(
    (el) => !el.closest('[inert]') && !el.hidden && (typeof el.checkVisibility !== 'function' || el.checkVisibility({ visibilityProperty: true })),
  );
}

export interface ModalOptions {
  /** Escape (the native cancel too), from anywhere inside the dialog. */
  onCancel: () => void;
  /** The control focus goes to first. */
  initialFocus?: RefObject<HTMLElement | null>;
}

export function useModal(ref: RefObject<HTMLDialogElement | null>, { onCancel, initialFocus }: ModalOptions): void {
  // The latest handler, read by the listeners installed once below.
  const cancel = useRef(onCancel);
  useLayoutEffect(() => {
    cancel.current = onCancel;
  });

  useLayoutEffect(() => {
    const dialog = ref.current;
    if (!dialog) return;
    const opener = document.activeElement instanceof HTMLElement ? document.activeElement : null;

    const quieted: Element[] = [];
    for (const el of Array.from(document.body.children)) {
      if (el === dialog || el.hasAttribute('inert')) continue;
      el.setAttribute('inert', '');
      quieted.push(el);
    }
    // jsdom has no showModal(); `open` draws it there, and the inert siblings above stand in for the top layer.
    if (typeof dialog.showModal === 'function') {
      if (!dialog.open) dialog.showModal();
    } else {
      dialog.setAttribute('open', '');
    }
    const first = initialFocus?.current ?? dialog.querySelector<HTMLElement>('input:not([type="hidden"]), select, textarea') ?? tabbables(dialog)[0] ?? null;
    first?.focus();

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.isComposing) return;
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        cancel.current();
        return;
      }
      if (e.key !== 'Tab') return;
      const items = tabbables(dialog);
      const head = items[0];
      const tail = items[items.length - 1];
      if (!head || !tail) {
        e.preventDefault();
        return;
      }
      const active = document.activeElement;
      const inside = active instanceof Node && dialog.contains(active);
      if (e.shiftKey && (active === head || !inside)) {
        e.preventDefault();
        tail.focus();
      } else if (!e.shiftKey && (active === tail || !inside)) {
        e.preventDefault();
        head.focus();
      }
    };
    // The browser's own Escape on a modal dialog: kept, and turned into the same cancel.
    const onNativeCancel = (e: Event) => {
      e.preventDefault();
      cancel.current();
    };
    dialog.addEventListener('keydown', onKeyDown);
    dialog.addEventListener('cancel', onNativeCancel);

    return () => {
      dialog.removeEventListener('keydown', onKeyDown);
      dialog.removeEventListener('cancel', onNativeCancel);
      if (typeof dialog.close === 'function' && dialog.open) dialog.close();
      for (const el of quieted) el.removeAttribute('inert');
      // After the page is live again: an inert element cannot take focus.
      if (opener && opener.isConnected) opener.focus();
    };
    // Once per opening: the dialog is mounted for one question and unmounted when it is answered.
  }, []);
}
